import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils/cn";

export function Dropdown({ trigger, children, align = "end", className }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const [left, setLeft] = useState(0);

  useLayoutEffect(() => {
    if (!open) return;
    const position = () => {
      const trigger = ref.current.getBoundingClientRect();
      const width = panelRef.current.offsetWidth;
      const preferred = align === "end" ? trigger.right - width : trigger.left;
      setLeft(Math.max(12, Math.min(preferred, window.innerWidth - width - 12)) - trigger.left);
    };
    position();
    window.addEventListener("resize", position);
    return () => window.removeEventListener("resize", position);
  }, [open, align]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <div onClick={() => setOpen((v) => !v)}>{trigger}</div>
      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            style={{ left, right: "auto", maxWidth: "calc(100vw - 24px)" }}
            role="menu"
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              "glass-panel absolute z-40 mt-2.5 min-w-[11rem] max-h-[70dvh] overflow-y-auto break-words rounded-[12px] border border-white/80 bg-white/95 p-2 text-popover-foreground shadow-[0_24px_60px_rgba(42,35,92,0.18)] backdrop-blur-2xl origin-top dark:border-white/10 dark:bg-slate-900/95",
              align === "end" ? "right-0" : "left-0",
              className
            )}
            onClick={() => setOpen(false)}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function DropdownItem({ className, icon: Icon, destructive, children, ...props }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        "flex w-full items-center gap-3 rounded-[12px] px-3 py-3 text-left text-[14px] font-semibold transition-all duration-150",
        destructive ? "text-danger hover:bg-danger-muted" : "text-slate-600 hover:bg-primary-muted hover:text-primary dark:text-slate-300",
        className
      )}
      {...props}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function DropdownLabel({ children }) {
  return <div className="px-0 pt-1 text-[12px] font-bold tracking-[0.1em] text-muted-foreground">{children}</div>;
}

export function DropdownSeparator() {
  return <div className="my-1.5 h-px bg-gradient-to-r from-transparent via-border to-transparent" role="separator" />;
}
